/**
 * The host's live: the guest's own room and door, in the brand's dark look, with the host's extras placed in it. The
 * session is the studio's (`useStudioSession`): the meeting, the room's socket, going live, ending, and the guard that
 * asks before leaving mid-live. Nothing here is a second room.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { guestLook } from "../../../shared/guest-look";
import type { ChatLine, ChatToRoom } from "../../../shared/live-protocol";
import { STUDIO_REFUSED } from "../../../shared/live-studio";
import type { LiveStudio, PublicBrand, WatchSession } from "../../../shared/types";
import { boardPath } from "../../board/paths";
import { api } from "../../lib/api";
import { refusedWords, cameraCheck, lightCheck, micCheck } from "../../live/meeting/checks";
import { useDoorEntry } from "../../live/meeting/useDoorEntry";
import { useMoments } from "../../live/useMoments";
import { HOST_FLOATS_PER_SEC, useRoomMood } from "../../live/useRoomMood";
import { clockWords, howItWentPath, nextSessionLine, opensLine, sessionLine, studioChip, topupWords } from "../../live/studioWords";
import { useStudioSession } from "../../live/useStudioSession";
import { useStudioPeople } from "../../live/useStudioPeople";
import { useLiveReactions } from "../Reactions";
import { Sheet, Toast, useGuestGround, useToast } from "../room/chrome";
import { usePickMenus } from "../room/pickMenu";
import type { WatchThread } from "../WatchPageView";
import { EndSheet, HostEnded, HostMoments, type HostKit, LeaveSheet } from "./hostParts";
import { type HostDoor, LiveDoor } from "./LiveDoor";
import { LiveRoom } from "./LiveRoom";
import "../../styles/guest.css";

const STAND_IN_BRAND: PublicBrand = { workspace_name: "Flashroom", crest: "FR", accent: "#148adf", brass: "#012556", paper: "#f5f5f5", logo_url: null };
const NO_THREAD = { messages: [], text: "", setText: () => {}, queue: [], trouble: "" } as unknown as WatchThread;
const NO_MOMENT = { up: null, answer: () => {}, dismiss: () => {} };
const CHIME_KEY = "fr-host-chime";

function readChime(): boolean {
  try {
    return localStorage.getItem(CHIME_KEY) !== "off";
  } catch {
    return true;
  }
}

export function HostLive({ info }: { info: LiveStudio }) {
  const navigate = useNavigate();
  const brand = info.brand ?? STAND_IN_BRAND;
  const look = useMemo(() => guestLook(brand), [brand]);
  useGuestGround(look);
  usePickMenus();
  const { toast, say } = useToast();
  const showToast = useCallback((words: string) => say(words, "check"), [say]);
  // Why a ref: the reactions are made before the session, and the session needs their floats.
  const sendNow = useRef<(msg: ChatToRoom) => boolean>(() => false);
  const reactSocket = useMemo(() => ({ send: (msg: ChatToRoom) => sendNow.current(msg) }), []);
  const reacts = useLiveReactions(reactSocket, HOST_FLOATS_PER_SEC);
  const mood = useRoomMood(true);
  const heard = useRef(reacts.heard);
  heard.current = (counts) => {
    reacts.heard(counts);
    mood.heard(counts);
  };
  const session = useStudioSession(info, showToast, (counts) => heard.current(counts));
  sendNow.current = session.send;
  const { meeting, state, chat, live, isLive, ended, now } = session;
  const hostFirst = info.hostName.split(" ")[0];
  const [ending, setEnding] = useState<"ask" | "busy" | null>(null);
  const [endError, setEndError] = useState("");
  const [next, setNext] = useState<string | null>(null);
  const [chime, setChimeState] = useState(readChime);
  const setChime = useCallback((on: boolean) => {
    setChimeState(on);
    try {
      localStorage.setItem(CHIME_KEY, on ? "on" : "off");
    } catch {
      // The choice then lasts until the page closes.
    }
  }, []);
  const back = info.published ? `/rooms/${info.webinarId}` : boardPath(info.webinarId);
  const report = howItWentPath(info);

  const endLive = async () => {
    setEnding("busy");
    setEndError("");
    try {
      const done = await session.end();
      if (info.series) setNext(nextSessionLine(done.next ?? null));
      setEnding(null);
    } catch (err) {
      setEnding("ask");
      setEndError(err instanceof Error ? err.message : "The live did not end. Try again.");
    }
  };

  const { runs, busy: momentBusy, act } = useMoments(info, chat.runs, showToast);
  const { pin, hide, replyPrivately, shareTools } = session;
  const lineTools = useMemo(() => ({ pin, hide, reply: (l: ChatLine) => void replyPrivately(l) }), [pin, hide, replyPrivately]);

  const kit: HostKit = {
    onEnd: () => setEnding("ask"),
    backTo: back,
    shareUrl: info.shareUrl ?? null,
    onCopy: () => {
      void navigator.clipboard?.writeText(info.shareUrl ?? "");
      showToast("Link copied");
    },
    onPauseRecording: (paused) =>
      void api.studio.pauseRecording(info.webinarId, paused, info.sessionId).catch((err: unknown) => showToast(err instanceof Error ? err.message : STUDIO_REFUSED.recordingUnchanged)),
    recordingSec: clockWords(session.liveSec),
    here: (chat.here ?? 0).toLocaleString("en-US"),
    mood: isLive ? mood.mood.words : "",
    onRule: shareTools.rule,
    onAnswer: shareTools.answer,
    lineTools,
    moments: info.moments.length ? (
      <HostMoments moments={info.moments} runs={runs} into={session.liveSec} wentLiveAt={isLive ? live.wentLiveAt : null} busy={momentBusy} onAct={act} />
    ) : null,
    chime: { on: chime, set: setChime },
  };

  const hostSession = useMemo(
    () => ({ host_name: info.hostName, host_avatar_url: info.hostPhoto, poster_url: null, title: info.title }) as unknown as WatchSession,
    [info],
  );
  const viewer = useMemo(() => ({ first: hostFirst, avatar: info.hostPhoto }), [hostFirst, info.hostPhoto]);

  // --- the door ---
  const rejoin = live.state === "live";
  const entry = useDoorEntry(state.phase, !rejoin, session.goIn);
  const { people, reload } = useStudioPeople(info.webinarId, info.sessionId, info.registered > 0);
  const [coming, setComing] = useState(false);
  const waitingCamera = state.media === "asking";
  const waiting = !rejoin && now >= Date.parse(live.opensAt) ? (chat.here ?? 0) : 0;
  const { camera, mic, brightness } = state.readings;
  const door: HostDoor = {
    button: rejoin ? "Join the live" : "Go live",
    going: rejoin ? "Going in" : "Going live",
    disabled: waitingCamera || !info.published || entry.busy,
    count: entry.count,
    checks: [
      { name: "Camera", ...cameraCheck(camera) },
      { name: "Microphone", ...micCheck(mic, state.microphones.length - 1) },
      { name: "Light", ...lightCheck(brightness) },
    ],
    troubles: [
      waitingCamera ? "Allow the camera and the microphone in the address bar." : null,
      state.media === "refused" ? refusedWords(camera, mic) : null,
      state.failure,
      info.published ? null : STUDIO_REFUSED.unpublished,
    ].filter((l): l is string => !!l),
    backWords: "Back",
    coming:
      info.registered > 0 ? (
        <>
          <button
            className="hk-coming"
            type="button"
            onClick={() => {
              reload();
              setComing(true);
            }}
          >
            {info.registered.toLocaleString("en-US")} registered{waiting > 0 ? `, ${waiting.toLocaleString("en-US")} waiting` : ""}. Who is coming
          </button>
          {info.shareUrl ? (
            <button className="hk-coming" type="button" onClick={kit.onCopy}>
              Copy the room link
            </button>
          ) : null}
        </>
      ) : (
        <p className="hk-note">No one has registered yet.</p>
      ),
  };

  // Why one effect: the live Worker's room only opens after the host goes in, and the studio's link guard asks first.
  useEffect(() => {
    document.title = `${info.title} · Studio`;
  }, [info.title]);

  let screen;
  if (ended) {
    screen = <HostEnded info={info} next={next} report={report} go={navigate} />;
  } else if (state.phase !== "in") {
    screen = (
      <LiveDoor
        brand={brand}
        title={info.title}
        when={[sessionLine(info), opensLine(live, now)].filter(Boolean).join(" · ") || "Your live"}
        withWords="with"
        hostName={info.hostName}
        hostAvatar={info.hostPhoto}
        hostFirst={hostFirst}
        live={isLive}
        startsIn={studioChip(live, now) || null}
        here={chat.here ?? 0}
        hostAway={false}
        faces={chat.faces}
        viewer={viewer}
        back={null}
        joining={entry.busy || state.phase === "joining"}
        meeting={meeting}
        state={state}
        onJoin={entry.go}
        slug=""
        siteKey={null}
        onSomeoneElse={() => {}}
        yourPage={back}
        poster={null}
        host={door}
      />
    );
  } else {
    screen = (
      <LiveRoom
        wait={false}
        brand={brand}
        session={hostSession}
        title={[info.title, sessionLine(info)].filter(Boolean).join(" · ")}
        sub={`with ${hostFirst}`}
        hostFirst={hostFirst}
        viewer={viewer}
        goesLiveAt=""
        clock={null}
        agenda={[]}
        meeting={meeting}
        state={state}
        chat={chat}
        send={session.send}
        reacts={reacts}
        thread={NO_THREAD}
        unreadHost={false}
        onReadHost={() => {}}
        opensAt=""
        liveAt={live.wentLiveAt}
        ended={false}
        onAfter={() => {}}
        onLeave={() => setEnding("ask")}
        moment={NO_MOMENT}
        offer={null}
        yourPage={back}
        host={kit}
      />
    );
  }

  return (
    <div className="gst" style={look} data-view="host" onPointerDown={() => meeting.wake()}>
      {screen}
      {session.leaving !== null ? (
        <LeaveSheet
          onEnd={() => {
            session.setLeaving(null);
            setEnding("ask");
          }}
          onKeep={() => {
            const to = session.leaving;
            if (to !== null) navigate(to);
          }}
          onStay={() => session.setLeaving(null)}
        />
      ) : null}
      {session.topupNotice ? (
        <div className="hk-topup" role="status">
          <b>{topupWords(session.topupNotice, live.timezone).title}</b>
          <span>{topupWords(session.topupNotice, live.timezone).body}</span>
          <span>Only you see this.</span>
          <button className="btn btn-g btn-s" type="button" onClick={session.closeTopupNotice}>
            Keep going
          </button>
        </div>
      ) : null}
      {ending ? <EndSheet busy={ending === "busy"} error={endError} onKeep={() => setEnding(null)} onGo={() => void endLive()} /> : null}
      {coming ? (
        <Sheet label="Who is coming" onClose={() => setComing(false)}>
          <div className="sx-in">
            <h3 className="sx-h">Who is coming</h3>
            <div className="hk-people">
              {(people ?? []).slice(0, 20).map((p) => (
                <p key={p.registrantId} className="hk-line">
                  <b>{p.name}</b>
                  <span>{waiting > 0 && p.here ? "Waiting" : "Registered"}</span>
                </p>
              ))}
              {info.registered > 20 ? <p className="hk-note">And {(info.registered - 20).toLocaleString("en-US")} more</p> : null}
            </div>
          </div>
        </Sheet>
      ) : null}
      <Toast toast={toast} />
    </div>
  );
}
